import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    slot: () => import('./locales/en/slot.json').then(m => m.default),
    common: () => import('./locales/en/common.json').then(m => m.default),
    games: () => import('./locales/en/games.json').then(m => m.default),
  },
  fr: {
    slot: () => import('./locales/fr/slot.json').then(m => m.default),
    common: () => import('./locales/fr/common.json').then(m => m.default),
    games: () => import('./locales/fr/games.json').then(m => m.default),
  },
  es: {
    slot: () => import('./locales/es/slot.json').then(m => m.default),
    common: () => import('./locales/es/common.json').then(m => m.default),
    games: () => import('./locales/es/games.json').then(m => m.default),
  },
  pt: {
    slot: () => import('./locales/pt/slot.json').then(m => m.default),
    common: () => import('./locales/pt/common.json').then(m => m.default),
    games: () => import('./locales/pt/games.json').then(m => m.default),
  },
  de: {
    slot: () => import('./locales/de/slot.json').then(m => m.default),
    common: () => import('./locales/de/common.json').then(m => m.default),
    games: () => import('./locales/de/games.json').then(m => m.default),
  },
  ru: {
    slot: () => import('./locales/ru/slot.json').then(m => m.default),
    common: () => import('./locales/ru/common.json').then(m => m.default),
    games: () => import('./locales/ru/games.json').then(m => m.default),
  },
  tr: {
    slot: () => import('./locales/tr/slot.json').then(m => m.default),
    common: () => import('./locales/tr/common.json').then(m => m.default),
    games: () => import('./locales/tr/games.json').then(m => m.default),
  },
  zh: {
    slot: () => import('./locales/zh/slot.json').then(m => m.default),
    common: () => import('./locales/zh/common.json').then(m => m.default),
    games: () => import('./locales/zh/games.json').then(m => m.default),
  },
  ja: {
    slot: () => import('./locales/ja/slot.json').then(m => m.default),
    common: () => import('./locales/ja/common.json').then(m => m.default),
    games: () => import('./locales/ja/games.json').then(m => m.default),
  },
  ko: {
    slot: () => import('./locales/ko/slot.json').then(m => m.default),
    common: () => import('./locales/ko/common.json').then(m => m.default),
    games: () => import('./locales/ko/games.json').then(m => m.default),
  },
};

// Detect language from URL param ?lang=xx (set by s&box client) or localStorage
function detectLanguage() {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams((window.location.hash.split('?')[1]) || '');
  return params.get('lang') || hashParams.get('lang') || localStorage.getItem('i18nLang') || 'en';
}

const lng = detectLanguage();

// Eagerly load the detected language + English fallback
const loadedResources = {};

async function loadLanguageResources(lang) {
  if (loadedResources[lang]) return loadedResources[lang];
  const loader = resources[lang];
  if (!loader) return null;
  const translations = {};
  for (const [ns, load] of Object.entries(loader)) {
    translations[ns] = await load();
  }
  loadedResources[lang] = translations;
  return translations;
}

async function initI18n() {
  // Load English (fallback) and detected language in parallel
  const loads = [loadLanguageResources('en')];
  if (lng !== 'en') loads.push(loadLanguageResources(lng));
  const [enData, lngData] = await Promise.all(loads);

  const i18nResources = {};
  if (enData) {
    i18nResources.en = enData;
  }
  if (lngData && lng !== 'en') {
    i18nResources[lng] = lngData;
  }

  await i18n
    .use(initReactI18next)
    .init({
      resources: i18nResources,
      lng,
      fallbackLng: 'en',
      defaultNS: 'common',
      ns: ['common', 'games', 'slot'],
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });

  // Persist language choice
  localStorage.setItem('i18nLang', lng);

  // Listen for hash changes from s&box WebPanel to switch language live
  window.addEventListener('hashchange', () => {
    const hashParams = new URLSearchParams((window.location.hash.split('?')[1]) || '');
    const newLang = hashParams.get('lang');
    if (newLang && newLang !== i18n.language) {
      changeLanguage(newLang);
    }
  });
}

// Lazy-load additional languages on demand
export async function changeLanguage(lang) {
  if (!loadedResources[lang]) {
    const data = await loadLanguageResources(lang);
    if (data) {
      for (const [ns, translations] of Object.entries(data)) {
        i18n.addResourceBundle(lang, ns, translations, true, true);
      }
    }
  }
  await i18n.changeLanguage(lang);
  localStorage.setItem('i18nLang', lang);
}

export const i18nReady = initI18n();
export default i18n;
